import React, { useState, useEffect } from 'react';
import Banner from './Banner';
import Projects from '../sections/Projects';
import DevPortfolio from '../sections/DevPortfolio';
import Navbar from './Navbar';
import EdgeTab from './EdgeTab';
import UpdatePills from './UpdatePills';
import Footer from './Footer';
import { useViewMode, hasExplicitViewMode } from '../../utils/viewMode';
import { trackEvent } from '../../utils/analytics';
import '../../styles/components/DualView.css';

/**
 * Persistent two-pane home experience.
 *
 * Splash model: a centered modal asks the visitor to pick a side
 * (Designer or Developer). The underlying dual-pane track is dimmed
 * and blurred behind the modal. On click, the modal fades out and the
 * track slides horizontally to land on the chosen pane (designer = 0,
 * developer = -100vw).
 *
 * Post-commit: each pane is its own scroll container. The EdgeTab on
 * the opposite edge flips between views with a horizontal slide.
 *
 * Footer lives inside each pane (forceMode prop hardcodes the palette
 * so it doesn't flicker mid-slide).
 */
const DualView = () => {
    const [mode, setMode] = useViewMode();
    const [hasChosen, setHasChosen] = useState(() => hasExplicitViewMode());
    const [slideSettled, setSlideSettled] = useState(false);

    const translateX = !hasChosen
        ? '-50vw'
        : mode === 'designer'
            ? '0'
            : '-100vw';

    const choose = (side) => {
        setMode(side);
        setHasChosen(true);
        trackEvent('splash_chosen', { mode: side });
    };

    // Track when the slide animation has settled — gates the App-level
    // background color flip so the canvas doesn't change colors mid-slide.
    useEffect(() => {
        if (!hasChosen) {
            setSlideSettled(false);
            return undefined;
        }
        setSlideSettled(false);
        const t = setTimeout(() => setSlideSettled(true), 750);
        return () => clearTimeout(t);
    }, [hasChosen, mode]);

    // Flip the App-level pastel gradient to flat GitHub-dark while
    // viewing the dev side. Gated on slideSettled so the gradient
    // doesn't change mid-slide.
    useEffect(() => {
        if (mode !== 'developer' || !slideSettled) return undefined;
        document.body.classList.add('dark-canvas');
        return () => document.body.classList.remove('dark-canvas');
    }, [mode, slideSettled]);

    // Lock body scroll during splash so the visitor can't accidentally
    // scroll the page while the modal is up. After splash commit, the
    // panes own scroll.
    useEffect(() => {
        if (hasChosen) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [hasChosen]);

    return (
        <div
            className={`DualView ${!hasChosen ? 'DualView--splash' : ''}`}
        >
            <div
                className="DualView__track"
                style={{ transform: `translateX(${translateX})` }}
            >
                <div className="DualView__pane DualView__pane--designer">
                    <div className="DualView__pane-content">
                        <Banner />
                        <Projects />
                        <Footer forceMode="designer" />
                    </div>
                    <div className="DualView__pane-veil" aria-hidden="true" />
                </div>
                <div className="DualView__pane DualView__pane--developer">
                    <div className="DualView__pane-content">
                        <Navbar />
                        <DevPortfolio />
                        <Footer forceMode="developer" />
                    </div>
                    <div className="DualView__pane-veil" aria-hidden="true" />
                </div>
            </div>

            {/* Splash modal — centered card with two side-pointed options.
                After commit, the whole overlay fades out (CSS) so the
                modal + backdrop don't snap-disappear during the slide. */}
            <div
                className={`DualView__splash ${hasChosen ? 'DualView__splash--hiding' : ''}`}
                role="dialog"
                aria-modal="true"
                aria-label="Choose portfolio view"
                aria-hidden={hasChosen}
            >
                <div className="DualView__splash-backdrop" aria-hidden="true" />
                <div className="DualView__splash-modal">
                    {/* Line-art illustration: laptop on a desk with a
                        plant, coffee mug, and sparkles. The laptop
                        screen shows a stylized portfolio mockup with a
                        pastel image block — a wink at the dual-pane
                        canvas the visitor is about to enter. */}
                    <svg
                        className="DualView__splash-illustration"
                        viewBox="30 50 600 330"
                        fill="none"
                        stroke="#1f2937"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <defs>
                            <linearGradient id="dvSplashImg" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#c7d2fe" />
                                <stop offset="50%" stopColor="#fbcfe8" />
                                <stop offset="100%" stopColor="#fed7aa" />
                            </linearGradient>
                        </defs>

                        {/* Soft background blob — gives the scene a sense of light/atmosphere */}
                        <ellipse cx="340" cy="200" rx="270" ry="155" fill="#f3f4f6" stroke="none" opacity="0.8" />

                        {/* Sparkle accents */}
                        <circle cx="195" cy="78" r="6" fill="#f9a8d4" stroke="none" />
                        <path
                            d="M 158 132 L 163 144 L 175 149 L 163 154 L 158 166 L 153 154 L 141 149 L 153 144 Z"
                            fill="#c4b5fd"
                            stroke="none"
                        />

                        {/* Plant — pot + stem + leaves */}
                        <path d="M 72 340 L 82 376 L 132 376 L 142 340 Z" />
                        <line x1="107" y1="340" x2="107" y2="220" />
                        {/* Left leaf */}
                        <path d="M 107 285 Q 70 270, 52 230 Q 88 252, 107 285 Z" />
                        {/* Upper-left leaf */}
                        <path d="M 107 245 Q 76 220, 70 185 Q 96 215, 107 245 Z" />
                        {/* Upper-right leaf */}
                        <path d="M 107 232 Q 145 215, 165 180 Q 130 220, 107 250 Z" />
                        {/* Right leaf */}
                        <path d="M 107 270 Q 145 255, 162 220 Q 135 260, 107 280 Z" />

                        {/* Laptop screen frame */}
                        <rect x="178" y="118" width="324" height="222" rx="10" />
                        {/* Browser top bar separator */}
                        <line x1="178" y1="148" x2="502" y2="148" />
                        {/* Window control dots */}
                        <circle cx="196" cy="133" r="3.5" />
                        <circle cx="210" cy="133" r="3.5" />
                        <circle cx="224" cy="133" r="3.5" />
                        {/* URL pill */}
                        <rect x="285" y="125" width="170" height="16" rx="8" />

                        {/* Hero image block (the pastel canvas wink) */}
                        <rect x="200" y="170" width="140" height="82" rx="6" fill="url(#dvSplashImg)" stroke="none" />
                        {/* Frame the image with a thin stroke */}
                        <rect x="200" y="170" width="140" height="82" rx="6" />

                        {/* Content text lines (right of image) */}
                        <line x1="360" y1="180" x2="480" y2="180" stroke="#d1d5db" strokeWidth="7" />
                        <line x1="360" y1="196" x2="478" y2="196" stroke="#d1d5db" strokeWidth="7" />
                        <line x1="360" y1="212" x2="448" y2="212" stroke="#d1d5db" strokeWidth="7" />

                        {/* Three project cards */}
                        <rect x="360" y="226" width="36" height="26" rx="4" />
                        <rect x="404" y="226" width="36" height="26" rx="4" />
                        <rect x="448" y="226" width="36" height="26" rx="4" />

                        {/* Caption lines under hero image */}
                        <line x1="200" y1="276" x2="356" y2="276" stroke="#d1d5db" strokeWidth="5" />
                        <line x1="200" y1="290" x2="318" y2="290" stroke="#d1d5db" strokeWidth="5" />
                        <line x1="200" y1="304" x2="340" y2="304" stroke="#d1d5db" strokeWidth="5" />

                        {/* Laptop base (trapezoid below the screen) */}
                        <path d="M 158 340 L 522 340 L 544 362 L 136 362 Z" />
                        {/* Trackpad notch */}
                        <path d="M 322 360 Q 340 364, 358 360" />

                        {/* Coffee mug */}
                        <path d="M 535 275 L 535 348 Q 535 360, 548 360 L 590 360 Q 602 360, 602 348 L 602 275 Z" />
                        <line x1="535" y1="288" x2="602" y2="288" />
                        {/* Mug handle */}
                        <path d="M 602 295 Q 622 295, 622 318 Q 622 340, 602 340" />
                        {/* Steam wisps */}
                        <path d="M 555 258 Q 560 250, 555 240" opacity="0.6" />
                        <path d="M 580 258 Q 585 248, 578 238" opacity="0.6" />

                        {/* Ground line */}
                        <line x1="40" y1="362" x2="620" y2="362" />
                    </svg>
                    <div className="DualView__splash-content">
                        <h2 className="DualView__splash-heading">Explore my work as a</h2>
                        <div className="DualView__splash-options">
                        <button
                            type="button"
                            className="DualView__splash-option DualView__splash-option--designer"
                            onClick={() => choose('designer')}
                            disabled={hasChosen}
                            tabIndex={hasChosen ? -1 : 0}
                        >
                            <span className="DualView__splash-option-arrow" aria-hidden="true">←</span>
                            <span>Designer</span>
                        </button>
                        <button
                            type="button"
                            className="DualView__splash-option DualView__splash-option--developer"
                            onClick={() => choose('developer')}
                            disabled={hasChosen}
                            tabIndex={hasChosen ? -1 : 0}
                        >
                            <span>Developer</span>
                            <span className="DualView__splash-option-arrow" aria-hidden="true">→</span>
                        </button>
                    </div>
                    </div>
                </div>
            </div>

            {hasChosen && <EdgeTab />}

            {/* Both of these must stay OUTSIDE .DualView__track: its
                transform is the containing block for fixed-position
                descendants, so a pill fixed inside a pane would anchor
                to the track rather than the viewport. Gated on
                hasChosen so the pills don't compete with the splash. */}
            {hasChosen && <UpdatePills />}
        </div>
    );
};

export default DualView;
